import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/components/language-provider";
import { useSettings } from "@/components/settings-provider";
import { Mascot, MascotMood, preloadMascot } from "@/components/assistant/mascot";
import { mascotJumpToChat } from "@/components/assistant/guide";
import { getPlanDisplayInfo } from "@/lib/plans";
import { getWebsiteUrl } from "@/lib/config";
import { Send, FileSignature, Building2, Check, Upload, Users, Sparkles, MessageCircle, PanelLeft, PanelTop, ArrowRight } from "lucide-react";
import "@/components/assistant/assistant.css";

const KEY = (userId: string) => `fs:onboarding:v1:${userId}`;
export const OPEN_ONBOARDING_EVENT = "fillsign:onboarding";

type Goal = "send" | "sign" | "dynamics";
type Step = 0 | 1 | 2 | 3;

interface PlanCard { id: string; name: string; price: string; period: string; description: string; features: string[]; contactSales: boolean; popular: boolean; annualPrice?: string; annualPerMonth?: string; savings?: number }

/**
 * First-login welcome led by the mascot: goal → quick preferences → 3-slide tour → the plan screen (free plan: the
 * three paid plans, the mascot pointing at each). Closing at any point still shows the plan screen once; closing that
 * makes the mascot jump into the chat launcher. Shown once per user (localStorage). Reopen with `?tour=1` on
 * /dashboard or `window.dispatchEvent(new Event(OPEN_ONBOARDING_EVENT))`.
 */
export function OnboardingDialog({ userId, plan, hasDocuments }: { userId?: string; plan?: string; hasDocuments?: boolean }) {
  const { t, language, setLanguage } = useLanguage();
  const { sidebarPlacement, setSidebarPlacement } = useSettings();
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>(0);
  const [goal, setGoal] = useState<Goal | null>(null);
  const [slide, setSlide] = useState(0);
  const [mood, setMood] = useState<MascotMood>("wave");
  const [speech, setSpeech] = useState<string | null>(null);
  const [pointAt, setPointAt] = useState(0);
  const [cycle, setCycle] = useState<"monthly" | "annual">("monthly");
  const mascotRef = useRef<HTMLDivElement>(null);
  const moodTimer = useRef<number | undefined>(undefined);
  const speechTimer = useRef<number | undefined>(undefined);
  const pt = language === "pt";
  const isFree = (plan ?? "free").toLowerCase() === "free";

  const { data: apiPlans } = useQuery<any[]>({ queryKey: ["/api/v1/plans"], enabled: open && step === 3, staleTime: 600_000 });

  const decided = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!userId || decided.current === userId) return;
    const forced = new URLSearchParams(window.location.search).get("tour") === "1";
    if (forced) { decided.current = userId; setOpen(true); return; }
    if (localStorage.getItem(KEY(userId))) { decided.current = userId; return; }
    // The "seen" flag lives in this browser only: on a new device, wait until the account's usage is known and skip
    // the first-login welcome for someone who already has documents.
    if (hasDocuments === undefined) return;
    decided.current = userId;
    if (hasDocuments) localStorage.setItem(KEY(userId), JSON.stringify({ skipped: "existing-account", at: new Date().toISOString() }));
    else setOpen(true);
  }, [userId, hasDocuments]);

  useEffect(() => {
    const onEvt = () => { setStep(0); setSlide(0); setOpen(true); };
    window.addEventListener(OPEN_ONBOARDING_EVENT, onEvt);
    return () => window.removeEventListener(OPEN_ONBOARDING_EVENT, onEvt);
  }, []);

  useEffect(() => { if (open) preloadMascot(); }, [open]);

  // What the mascot does on each step.
  useEffect(() => {
    if (!open) return;
    const moods: Record<number, MascotMood> = { 0: "wave", 1: "idle", 2: (["writing", "wave", "happy"] as MascotMood[])[slide] ?? "idle", 3: isFree ? "point" : "happy" };
    feel(moods[step] ?? "idle");
    // A reaction line from a poke does not outlive the step it was said on.
    window.clearTimeout(speechTimer.current);
    setSpeech(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step, slide]);

  // Plan screen: the mascot points at one plan after the other.
  useEffect(() => {
    if (!open || step !== 3 || !isFree) return;
    const id = window.setInterval(() => setPointAt((p) => (p + 1) % 3), 1800);
    return () => window.clearInterval(id);
  }, [open, step, isFree]);

  useEffect(() => () => { window.clearTimeout(moodTimer.current); window.clearTimeout(speechTimer.current); }, []);

  const feel = (m: MascotMood, ms?: number) => {
    window.clearTimeout(moodTimer.current);
    setMood(m);
    if (ms) moodTimer.current = window.setTimeout(() => setMood(step === 3 && isFree ? "point" : "idle"), ms);
  };
  const say = (text: string, ms = 2600) => {
    window.clearTimeout(speechTimer.current);
    setSpeech(text);
    speechTimer.current = window.setTimeout(() => setSpeech(null), ms);
  };

  const poke = () => {
    const reactions: MascotMood[] = ["happy", "surprised", "wave"];
    feel(reactions[Math.floor(Math.random() * reactions.length)], 1500);
    const lines = pt
      ? ["Hehe! Qualquer dúvida, me chama no chat ali embaixo.", "Oi! Eu fico no cantinho da tela, é só clicar em Chat.", "Pode perguntar o que quiser no chat, eu procuro pra você!"]
      : ["Hehe! Any question, call me in the chat down there.", "Hi! I live in the corner of the screen, just click Chat.", "Ask me anything in the chat, I'll look it up!"];
    say(lines[Math.floor(Math.random() * lines.length)], 3200);
  };

  const remember = () => { if (userId) localStorage.setItem(KEY(userId), JSON.stringify({ goal, at: new Date().toISOString() })); };

  /** Any "skip"/close before the end goes to the plan screen; closing the plan screen ends with the jump into the chat. */
  const skipToEnd = () => { remember(); setStep(3); };

  const finish = async () => {
    remember();
    const from = mascotRef.current?.getBoundingClientRect();
    setOpen(false);
    if (from) mascotJumpToChat(from);
    const go = goal === "sign" ? "/signatures" : goal === "dynamics" ? "/settings" : goal === "send" ? "/documents/new" : null;
    if (go) window.setTimeout(() => navigate(go), from ? 800 : 0);
  };

  const goals: Array<[Goal, typeof Send, string]> = [
    ["send", Send, t("onboarding.goalSend")],
    ["sign", FileSignature, t("onboarding.goalSign")],
    ["dynamics", Building2, t("onboarding.goalDynamics")],
  ];
  const slides: Array<[typeof Upload, string, string]> = [
    [Upload, t("onboarding.tour1Title"), t("onboarding.tour1Desc")],
    [Users, t("onboarding.tour2Title"), t("onboarding.tour2Desc")],
    [Sparkles, t("onboarding.tour3Title"), t("onboarding.tour3Desc")],
  ];
  const stepLabels = [t("onboarding.step1"), t("onboarding.step2"), t("onboarding.step3"), pt ? "Seu plano" : "Your plan"];
  const firstName = (() => { try { return JSON.parse(localStorage.getItem("user") || "{}").firstName?.split(" ")[0] || ""; } catch { return ""; } })();

  const bubble = speech ?? (
    step === 0 ? (pt ? `Oi${firstName ? `, ${firstName}` : ""}! Estou aqui para ajudar.` : `Hi${firstName ? `, ${firstName}` : ""}! I'm here to help.`)
    : step === 1 ? (pt ? "Como você prefere o app? Dá pra mudar depois em Configurações." : "How do you like the app? You can change it later in Settings.")
    : step === 2 ? (slide === 0 ? (pt ? "Mandar um documento é assim: sobe o arquivo e eu cuido do resto." : "Sending is simple: upload the file and I handle the rest.")
      : slide === 1 ? (pt ? "Quem assina recebe o link por e-mail (e WhatsApp, se quiser)." : "Signers get the link by e-mail (and WhatsApp, if you want).")
      : (pt ? "Quando todos assinam, o PDF assinado chega pra todo mundo. 🎉" : "When everyone signs, the signed PDF reaches everyone. 🎉"))
    : isFree ? (pt ? "Você está no plano gratuito: 3 documentos por mês. Se quiser mais, é só escolher um destes 👉" : "You're on the free plan: 3 documents a month. Want more? Pick one of these 👉")
    : (pt ? "Tudo pronto! Qualquer dúvida, me chama no chat ali embaixo." : "All set! Any question, call me in the chat down there.")
  );

  const plans: PlanCard[] = (apiPlans && apiPlans.length > 0
    ? apiPlans.filter((p) => String(p.planType).toLowerCase() !== "free").map((p) => {
      let features: string[] = [];
      try { const f = typeof p.features === "string" ? JSON.parse(p.features) : p.features; features = (Array.isArray(f) ? f : []).filter((x: any) => x.included !== false).map((x: any) => (pt ? x.pt : x.en)).filter(Boolean); } catch { /* no features */ }
      if (features.length === 0) features = [p.documentLimit === -1 ? (pt ? "Documentos ilimitados" : "Unlimited documents") : (pt ? `${p.documentLimit} documentos/mês` : `${p.documentLimit} documents/month`),
        p.userLimit === -1 ? (pt ? "Usuários ilimitados" : "Unlimited users") : (pt ? `${p.userLimit} usuário(s)` : `${p.userLimit} user(s)`)];
      const monthly = Number(pt ? p.priceMonthlyBrl : p.priceMonthlyUsd) || 0;
      const annual = Number(pt ? p.priceAnnualBrl : p.priceAnnualUsd) || 0;
      const money = (n: number) => pt ? `R$ ${n.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}` : `$${n.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
      return {
        id: String(p.planType).toLowerCase(), name: p.name,
        price: p.contactSales ? (pt ? "Sob consulta" : "Contact us") : money(monthly),
        period: p.contactSales ? "" : (pt ? "/mês" : "/month"),
        description: pt ? p.descriptionPt : p.descriptionEn,
        features: features.slice(0, 4), contactSales: !!p.contactSales, popular: !!p.popular,
        annualPrice: annual > 0 && !p.contactSales ? money(annual) : undefined,
        annualPerMonth: annual > 0 && !p.contactSales ? `≈ ${money(Math.round(annual / 12 * 100) / 100)}${pt ? "/mês" : "/month"}` : undefined,
        savings: annual > 0 && monthly > 0 ? Math.max(0, Math.round((1 - annual / (monthly * 12)) * 100)) : 0,
      };
    })
    : getPlanDisplayInfo().filter((p) => p.id !== "free").map((p) => ({
      id: p.id, name: p.name, price: pt ? p.price.pt : p.price.en, period: pt ? p.period.pt : p.period.en, description: pt ? p.description.pt : p.description.en,
      features: p.features.filter((f) => f.included).map((f) => (pt ? f.pt : f.en)).slice(0, 4), contactSales: !!p.contactSales, popular: !!p.popular,
    }))).slice(0, 3);

  const choosePlan = (p: PlanCard) => {
    remember();
    setOpen(false);
    if (p.contactSales) window.location.href = getWebsiteUrl("/enterprise-contact?plan=custom");
    else navigate(`/settings?action=plans${cycle === "annual" && p.annualPrice ? "&cycle=annual" : ""}`);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { if (step === 3) void finish(); else skipToEnd(); } }}>
      <DialogContent className="max-w-3xl gap-0 overflow-hidden p-0 sm:rounded-2xl" data-testid="onboarding-dialog">
        <div className="grid sm:grid-cols-[230px_1fr]">
          {/* mascot rail */}
          <aside className="flex flex-col items-center gap-3 border-b bg-muted/40 p-5 sm:border-b-0 sm:border-r" data-testid="onboarding-rail">
            <div ref={mascotRef} className="relative">
              <Mascot mood={mood} size={128} onClick={poke} title="FillSign" />
            </div>
            <p className="fs-assistant-speech max-w-[200px] rounded-2xl rounded-tl-sm border bg-card px-3 py-2 text-center text-sm shadow-sm" data-testid="onboarding-speech">{bubble}</p>
            <ol className="mt-auto hidden w-full space-y-3 sm:block">
              {stepLabels.map((l, i) => (
                <li key={l} className={`border-l-2 pl-3 text-xs ${i === step ? "border-primary text-foreground" : i < step ? "border-primary/40 text-muted-foreground" : "border-border text-muted-foreground"}`}>{l}</li>
              ))}
            </ol>
          </aside>

          {/* content */}
          <section className="p-6 sm:p-8">
            {step === 0 && (
              <>
                <DialogHeader className="text-left">
                  <DialogTitle className="text-2xl">{t("onboarding.welcomeTitle")}</DialogTitle>
                  <DialogDescription>{t("onboarding.welcomeDesc")}</DialogDescription>
                </DialogHeader>
                <div className="mt-6 space-y-3">
                  {goals.map(([id, Icon, label]) => (
                    <button key={id} type="button" onClick={() => { setGoal(id); feel("happy", 1200); }} data-testid={`onboarding-goal-${id}`}
                      className={`flex w-full items-center gap-3 rounded-xl border p-4 text-left transition-colors ${goal === id ? "border-primary bg-primary/5" : "hover:bg-muted/50"}`}>
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="h-4 w-4" /></span>
                      <span className="flex-1 text-sm font-medium">{label}</span>
                      <span className={`flex h-5 w-5 items-center justify-center rounded-full border ${goal === id ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40"}`}>{goal === id && <Check className="h-3 w-3" />}</span>
                    </button>
                  ))}
                </div>
                <div className="mt-8 flex justify-between">
                  <Button variant="ghost" onClick={skipToEnd} data-testid="onboarding-skip">{t("onboarding.skip")}</Button>
                  <Button disabled={!goal} onClick={() => setStep(1)} data-testid="onboarding-next">{t("common.next")}</Button>
                </div>
              </>
            )}

            {step === 1 && (
              <>
                <DialogHeader className="text-left">
                  <DialogTitle className="text-2xl">{t("onboarding.prefsTitle")}</DialogTitle>
                  <DialogDescription>{t("onboarding.prefsDesc")}</DialogDescription>
                </DialogHeader>
                <div className="mt-6 space-y-5">
                  <div>
                    <div className="mb-2 text-sm font-medium">{t("onboarding.language")}</div>
                    <div className="flex gap-2">
                      {(["pt", "en"] as const).map((l) => (
                        <Button key={l} type="button" variant={language === l ? "default" : "outline"} onClick={() => setLanguage(l)}>{l === "pt" ? "Português" : "English"}</Button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="mb-2 text-sm font-medium">{t("onboarding.menu")}</div>
                    <div className="flex gap-2">
                      <Button type="button" variant={sidebarPlacement === "left" ? "default" : "outline"} onClick={() => setSidebarPlacement("left")}><PanelLeft className="mr-2 h-4 w-4" />{t("onboarding.menuLeft")}</Button>
                      <Button type="button" variant={sidebarPlacement === "top" ? "default" : "outline"} onClick={() => setSidebarPlacement("top")}><PanelTop className="mr-2 h-4 w-4" />{t("onboarding.menuTop")}</Button>
                    </div>
                  </div>
                  <p className="flex items-start gap-2 rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground"><MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#1B7F3B]" />{t("onboarding.whatsappHint")}</p>
                </div>
                <div className="mt-8 flex justify-between">
                  <Button variant="ghost" onClick={() => setStep(0)}>{t("common.back")}</Button>
                  <Button onClick={() => setStep(2)} data-testid="onboarding-next">{t("common.next")}</Button>
                </div>
              </>
            )}

            {step === 2 && (() => { const [Icon, title, desc] = slides[slide]; return (
              <>
                <DialogHeader className="text-left">
                  <DialogTitle className="text-2xl">{t("onboarding.tourTitle")}</DialogTitle>
                  <DialogDescription>{t("onboarding.tourDesc")}</DialogDescription>
                </DialogHeader>
                <div className="mt-6 rounded-xl border bg-muted/30 p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="h-5 w-5" /></span>
                  <h3 className="mt-4 text-lg font-semibold">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{desc}</p>
                  <div className="mt-5 flex gap-1.5">{slides.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === slide ? "w-6 bg-primary" : "w-1.5 bg-muted-foreground/30"}`} />)}</div>
                </div>
                <div className="mt-8 flex justify-between">
                  <Button variant="ghost" onClick={() => (slide === 0 ? setStep(1) : setSlide(slide - 1))}>{t("common.back")}</Button>
                  {slide < slides.length - 1
                    ? <Button onClick={() => setSlide(slide + 1)} data-testid="onboarding-next">{t("common.next")}</Button>
                    : <Button onClick={() => { remember(); setStep(3); }} data-testid="onboarding-finish">{pt ? "Concluir" : "Finish"}</Button>}
                </div>
              </>
            ); })()}

            {step === 3 && (
              <>
                <DialogHeader className="text-left">
                  <DialogTitle className="text-2xl">{isFree ? (pt ? "Seu plano hoje: gratuito" : "Your plan today: free") : (pt ? "Tudo pronto!" : "All set!")}</DialogTitle>
                  <DialogDescription>
                    {isFree
                      ? (pt ? "3 documentos grátis todo mês. Quando precisar de mais, estes planos estão aqui — sem pressa." : "3 free documents every month. When you need more, these plans are here, no rush.")
                      : (pt ? "Qualquer dúvida, use o chat no canto da tela." : "Any question, use the chat at the corner of the screen.")}
                  </DialogDescription>
                </DialogHeader>
                {isFree && plans.some((p) => p.annualPrice) && (
                  <div className="mt-4 inline-flex rounded-full border bg-muted/40 p-1" data-testid="onboarding-cycle">
                    <button type="button" onClick={() => setCycle("monthly")} className={`rounded-full px-3 py-1 text-xs font-medium ${cycle === "monthly" ? "bg-background shadow" : "text-muted-foreground"}`} aria-pressed={cycle === "monthly"} data-testid="onboarding-cycle-monthly">{pt ? "Mensal" : "Monthly"}</button>
                    <button type="button" onClick={() => setCycle("annual")} className={`rounded-full px-3 py-1 text-xs font-medium ${cycle === "annual" ? "bg-background shadow" : "text-muted-foreground"}`} aria-pressed={cycle === "annual"} data-testid="onboarding-cycle-annual">
                      {pt ? "Anual" : "Annual"}{Math.max(0, ...plans.map((p) => p.savings || 0)) > 0 && <span className="ml-1 text-emerald-700 dark:text-emerald-300">-{Math.max(0, ...plans.map((p) => p.savings || 0))}%</span>}
                    </button>
                  </div>
                )}
                {isFree ? (
                  <div className="mt-5 grid gap-3 sm:grid-cols-3" data-testid="onboarding-plans">
                    {plans.map((p, i) => (
                      <div key={p.id} className={`relative flex flex-col rounded-xl border p-3 transition-all duration-300 ${pointAt === i ? "border-primary shadow-md ring-2 ring-primary/30" : "border-border"}`} data-testid={`onboarding-plan-${p.id}`}>
                        {pointAt === i && <span className="absolute -left-3 top-3 hidden sm:block"><Mascot mood="point" size={28} /></span>}
                        <p className="font-semibold">{p.name}</p>
                        <p className="text-xs text-muted-foreground">{p.description}</p>
                        {cycle === "annual" && p.annualPrice ? (
                          <p className="mt-2 text-xl font-bold">{p.annualPrice}<span className="text-xs font-normal text-muted-foreground">{pt ? "/ano" : "/year"}</span><span className="block text-xs font-normal text-muted-foreground">{p.annualPerMonth}</span></p>
                        ) : (
                          <p className="mt-2 text-xl font-bold">{p.price}<span className="text-xs font-normal text-muted-foreground">{p.period}</span></p>
                        )}
                        <ul className="mt-2 flex-1 space-y-1 text-xs text-muted-foreground">
                          {p.features.map((f) => <li key={f} className="flex items-start gap-1"><Check className="mt-0.5 h-3 w-3 shrink-0 text-primary" />{f}</li>)}
                        </ul>
                        <Button size="sm" variant={p.contactSales ? "outline" : "default"} className="mt-3" onClick={() => choosePlan(p)} data-testid={`onboarding-choose-${p.id}`}>
                          {p.contactSales ? (pt ? "Falar com vendas" : "Contact sales") : (pt ? "Escolher" : "Choose")}
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-6 rounded-xl border bg-muted/30 p-6 text-sm text-muted-foreground">
                    {pt ? "Documentos, assinaturas, auditoria, relatórios: é só pedir no chat." : "Documents, signatures, audit trail, reports: just ask in the chat."}
                  </div>
                )}
                <div className="mt-6 flex justify-end">
                  <Button onClick={() => void finish()} data-testid="onboarding-done">
                    {goal === "send" ? (pt ? "Enviar meu primeiro documento" : "Send my first document") : (pt ? "Começar" : "Get started")} <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
